(() => {
  'use strict';
  try{
    if(location.pathname!=='/challenge'&&sessionStorage.getItem('motmLeagueSubmissionPending')) history.scrollRestoration='manual';
  }catch(_){}
  const ready = fn => document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', fn, {once:true}) : fn();
  const interactive = "a,button,summary,input,select,textarea,[role='button']";
  const parseColor = value => { const n=value?.match(/[\d.]+/g)?.map(Number); return !n||n.length<3?null:{r:n[0],g:n[1],b:n[2],a:n[3]??1}; };
  const luma = (r,g,b)=>(r*299+g*587+b*114)/1000;
  function textElementAtPoint(x,y){
    const range=document.caretRangeFromPoint?.(x,y); if(!range||range.startContainer.nodeType!==Node.TEXT_NODE)return null;
    const node=range.startContainer,text=node.data;if(!text.trim())return null;
    for(const index of [Math.min(range.startOffset,text.length-1),Math.max(0,range.startOffset-1)]){
      if(!text[index]?.trim())continue;const r=document.createRange();r.setStart(node,index);r.setEnd(node,index+1);
      if([...r.getClientRects()].some(rect=>x>=rect.left-2&&x<=rect.right+2&&y>=rect.top-2&&y<=rect.bottom+2))return node.parentElement;
    } return null;
  }
  function isDarkAtPoint(el,x,y){
    if(!el)return false;

    /* A painted interactive surface wins first.
       This fixes white/lime buttons sitting on black sections. */
    const interactiveEl=el.closest?.(interactive);
    if(interactiveEl){
      const bg=parseColor(getComputedStyle(interactiveEl).backgroundColor);
      if(bg&&bg.a>.28)return luma(bg.r,bg.g,bg.b)<145;
    }

    /* Text colour is an inverse clue to the surface beneath it:
       light text normally means a dark surface; dark text means light. */
    const textEl=textElementAtPoint(x,y);
    if(textEl){
      const tc=parseColor(getComputedStyle(textEl).color);
      if(tc&&tc.a>.2)return luma(tc.r,tc.g,tc.b)>165;
    }

    /* Explicit section intent is the next strongest signal. */
    const themed=el.closest?.('[data-cursor-theme]')?.dataset.cursorTheme;
    if(themed==='dark')return true;
    if(themed==='light')return false;

    /* Finally composite visible ancestor backgrounds. */
    let n=el,alpha=0,r=0,g=0,b=0;
    while(n){
      const c=parseColor(getComputedStyle(n).backgroundColor);
      if(c&&c.a>0){
        const part=(1-alpha)*c.a;
        r+=c.r*part;g+=c.g*part;b+=c.b*part;alpha+=part;
        if(alpha>=.96)break;
      }
      n=n.parentElement;
    }
    return alpha===0?false:luma(r/alpha,g/alpha,b/alpha)<145;
  }
  function initCursor(){
    const c=document.getElementById('motm-cursor'),media=matchMedia('(hover:hover) and (pointer:fine) and (prefers-reduced-motion:no-preference)');if(!c||!media.matches)return;
    document.documentElement.classList.add('motm-cursor-enabled');let raf=0,x=-60,y=-60;

    /* Hit-testing the point under the cursor and compositing the surface colour
       beneath it both force style resolution, so they run once per animation
       frame rather than once per pointermove — the event fires far more often
       than the screen updates, and only the last position of a frame is drawn. */
    const paint=()=>{
      const target=document.elementFromPoint(x,y);
      c.classList.toggle('is-active',!!target?.closest?.(interactive));
      c.classList.toggle('is-on-dark',isDarkAtPoint(target,x,y));
      c.style.transform=`translate3d(${x}px,${y}px,0) translate(-50%,-50%)`;
      raf=0;
    };
    addEventListener('pointermove',e=>{x=e.clientX;y=e.clientY;c.classList.add('is-visible');if(!raf)raf=requestAnimationFrame(paint);},{passive:true});
    addEventListener('pointerdown',()=>c.classList.add('is-down'),{passive:true});addEventListener('pointerup',()=>c.classList.remove('is-down'),{passive:true});
    document.documentElement.addEventListener('mouseleave',()=>c.classList.remove('is-visible','is-down','is-active','is-on-dark'));addEventListener('blur',()=>c.classList.remove('is-visible'));
  }
  function initMarquees(){
    document.querySelectorAll('[data-motm-marquee]').forEach(el=>{
      if(el.dataset.ready)return;
      const group=el.querySelector('.motm-marquee__group');
      if(!group)return;
      const makeInert=node=>{
        node.removeAttribute?.('data-shopify-editor-block');
        node.setAttribute?.('aria-hidden','true');
        if(node.matches?.('a,button,input,select,textarea,[tabindex]'))node.setAttribute('tabindex','-1');
        node.querySelectorAll?.('a,button,input,select,textarea,[tabindex]').forEach(n=>n.setAttribute('tabindex','-1'));
        return node;
      };
      const originals=[...group.children].map(node=>node.cloneNode(true));
      const target=Math.max(innerWidth*1.12,900);
      let guard=0;
      while(group.getBoundingClientRect().width<target&&guard<6){
        originals.forEach(node=>group.appendChild(makeInert(node.cloneNode(true))));
        guard++;
      }
      const clone=makeInert(group.cloneNode(true));
      el.appendChild(clone);
      const width=group.getBoundingClientRect().width;
      const dur=Math.max(22,width/68);
      el.style.setProperty('--motm-marquee-duration',`${dur.toFixed(2)}s`);
      el.dataset.ready='1';
    });
  }
  function initAdaptiveHeader(){
    const group=document.getElementById('header-group');
    const header=group?.querySelector('[data-motm-adaptive-header]');
    if(!group||!header||group.dataset.motmAdaptiveReady==='1')return;
    group.dataset.motmAdaptiveReady='1';

    /* Store the original location for Shopify Theme Editor only. Storefront
       pages portal the complete group once, so it cannot be clipped by the
       theme's internal .page-wrapper scrolling context. */
    const originalParent=group.parentNode;
    const originalNext=group.nextSibling;
    const designMode=Boolean(window.Shopify?.designMode);

    if(!designMode&&group.parentNode!==document.body){
      document.body.insertBefore(group,document.querySelector('.page-wrapper')||document.body.firstChild);
    }
    group.classList.add('motm-nav-stack');

    const scrollPort=document.querySelector('.page-wrapper');
    let raf=0;

    const scrollTop=()=>Math.max(
      0,
      Number(scrollPort?.scrollTop||0),
      Number(document.scrollingElement?.scrollTop||0),
      Number(window.scrollY||0)
    );

    const measure=()=>{
      const height=Math.ceil(group.getBoundingClientRect().height||0);
      if(height>0)document.body.style.setProperty('--motm-nav-stack-height',`${height}px`);
    };

    const paint=()=>{
      const scrolled=scrollTop()>8;
      document.body.classList.toggle('motm-nav-scrolled',scrolled);
      header.classList.toggle('is-fixed',scrolled);
      header.classList.toggle('is-opening',!scrolled);
      header.setAttribute('data-sticky-state',scrolled?'active':'inactive');
      const headerHeight=Math.ceil(header.getBoundingClientRect().height||0);
      header.style.setProperty('--motm-drawer-top',`${headerHeight}px`);
      measure();
      raf=0;
    };

    const requestPaint=()=>{if(!raf)raf=requestAnimationFrame(paint);};
    scrollPort?.addEventListener('scroll',requestPaint,{passive:true});
    window.addEventListener('scroll',requestPaint,{passive:true});
    window.addEventListener('resize',()=>requestAnimationFrame(()=>{measure();paint();}),{passive:true});
    window.addEventListener('orientationchange',()=>requestAnimationFrame(()=>{measure();paint();}),{passive:true});
    window.addEventListener('pageshow',()=>requestAnimationFrame(()=>{measure();paint();}),{passive:true});

    if('ResizeObserver' in window){
      const ro=new ResizeObserver(()=>requestAnimationFrame(measure));
      ro.observe(group);
    }

    document.addEventListener('shopify:section:load',()=>requestAnimationFrame(()=>{measure();paint();}));
    document.addEventListener('shopify:section:reorder',()=>requestAnimationFrame(()=>{measure();paint();}));

    /* Theme Editor keeps the section group in its native DOM location so Lee
       can select/reorder blocks normally. Storefront preview uses the portal. */
    if(designMode&&originalParent&&group.parentNode!==originalParent){
      originalParent.insertBefore(group,originalNext);
    }

    requestAnimationFrame(()=>{measure();paint();});
  }
  function initFormSuccess(root=document){
    const target=root.querySelector?.('[data-motm-form-success]');
    if(!target||target.dataset.motmSuccessReady==='1')return;
    target.dataset.motmSuccessReady='1';
    const reduce=matchMedia('(prefers-reduced-motion: reduce)').matches;
    requestAnimationFrame(()=>{
      target.scrollIntoView({behavior:reduce?'auto':'smooth',block:'center'});
      try{target.focus({preventScroll:true});}catch(_){target.focus?.();}
    });
  }
  let leagueCartDetails=null;

  function readLeagueSessionMarker(key,maxAge){
    try{
      const raw=sessionStorage.getItem(key);
      if(!raw)return null;
      const marker=JSON.parse(raw);
      if(!marker||!marker.at)return null;
      const age=Date.now()-Number(marker.at);
      if(age<0||age>maxAge){
        sessionStorage.removeItem(key);
        return null;
      }
      return marker;
    }catch(_){
      try{sessionStorage.removeItem(key);}catch(__){}
      return null;
    }
  }

  function getLeagueSubmissionMarker(){
    return readLeagueSessionMarker('motmLeagueSubmissionPending',15*60*1000);
  }

  function clearLeagueSubmissionMarker(){
    try{sessionStorage.removeItem('motmLeagueSubmissionPending');}catch(_){}
  }

  function getLeaguePaymentPending(){
    return readLeagueSessionMarker('motmLeaguePaymentPending',2*60*60*1000);
  }

  /* Browser storage keeps only what the returning UI needs to decide what to
     show and where to scroll. The registrant's details live in the cart line,
     which Shopify holds server-side, and are read back from there. */
  function strippedMarker(marker){
    return {at:Date.now(),scrollY:Number(marker?.scrollY)||0,method:marker?.method||''};
  }

  function setLeaguePaymentPending(marker){
    if(!marker)return;
    try{sessionStorage.setItem('motmLeaguePaymentPending',JSON.stringify(strippedMarker(marker)));}catch(_){}
  }

  const LEAGUE_PROPERTY_MAP={name:'Name',phone:'WhatsApp',email:'Email',preference:'Level',wppr:'WPPR ranking',method:'Registration'};

  async function leagueDetailsFromCart(section){
    const variantId=Number(section?.dataset.registrationVariantId||0);
    if(!variantId)return null;
    try{
      const cart=await fetchLeagueCart(window.Shopify?.routes?.root||'/');
      const line=(cart.items||[]).find(item=>Number(item.variant_id)===variantId);
      if(!line)return null;
      const details={};
      Object.entries(LEAGUE_PROPERTY_MAP).forEach(([key,property])=>{
        details[key]=line.properties?.[property]||'';
      });
      return details;
    }catch(_){
      return null;
    }
  }

  function clearLeaguePaymentPending(){
    try{sessionStorage.removeItem('motmLeaguePaymentPending');}catch(_){}
  }

  function restoreLeagueViewport(marker){
    const top=Number(marker?.scrollY);
    if(!Number.isFinite(top)||top<0)return false;
    const restore=()=>scrollTo({top,left:0,behavior:'auto'});
    restore();
    requestAnimationFrame(()=>requestAnimationFrame(restore));
    setTimeout(restore,80);
    setTimeout(()=>{try{history.scrollRestoration='auto';}catch(_){}},260);
    return true;
  }

  function leagueMethodRequiresPayment(section,method){
    const panel=section?.querySelector?.('[data-motm-league-payment-panel]');
    const acknowledgement=section?.querySelector?.('[data-motm-payment-ack]');
    const standard=(panel?.dataset.standardMethod||acknowledgement?.dataset.standardMethod||'').trim().toLowerCase();
    const selected=(method||'').trim().toLowerCase();
    return Boolean(standard&&selected&&standard===selected);
  }

  function setLeaguePaymentPanel(section,marker,{scroll=false}={}){
    const panel=section?.querySelector?.('[data-motm-league-payment-panel]');
    if(!panel)return false;
    const show=Boolean(marker&&leagueMethodRequiresPayment(section,marker.method));
    panel.hidden=!show;
    if(!show)return false;

    const props={
      name:marker.name||'',
      phone:marker.phone||'',
      email:marker.email||'',
      preference:marker.preference||'',
      wppr:marker.wppr||'',
      method:marker.method||''
    };
    Object.entries(props).forEach(([key,value])=>{
      const target=panel.querySelector(`[data-motm-payment-property="${key}"]`);
      if(target&&value)target.value=value;
    });

    if(scroll){
      restoreLeagueViewport(marker);
    }
    return true;
  }

  function leagueMarkerFromForm(form){
    const value=name=>form.querySelector(`[name="${name}"]`)?.value||'';
    return {
      at:Date.now(),
      scrollY:window.scrollY,
      name:value('contact[name]'),
      phone:value('contact[phone]'),
      email:value('contact[email]'),
      preference:value('contact[league_preference]'),
      wppr:value('contact[verified_wppr_ranking]'),
      method:value('contact[registration_method]')
    };
  }

  function notifyLeagueCartUpdate(cart){
    try{
      const event=new CustomEvent('shopify:cart:lines-update');
      event.promise=Promise.resolve({detail:{itemCount:Number(cart?.item_count||0)}});
      document.dispatchEvent(event);
    }catch(_){}
  }

  async function fetchLeagueCart(root){
    const response=await fetch(`${root}cart.js`,{
      headers:{Accept:'application/json'},
      credentials:'same-origin'
    });
    if(!response.ok)throw new Error('Cart could not be loaded.');
    return response.json();
  }

  async function prepareLeaguePaidRegistration(section,marker){
    const root=window.Shopify?.routes?.root||'/';
    const variantId=Number(section?.dataset.registrationVariantId||0);
    const available=String(section?.dataset.registrationAvailable||'').toLowerCase()==='true';
    if(!variantId||!available)throw new Error('League registration is currently unavailable. Please contact MOTM.');

    /* This product is dedicated to one League registration per browser/cart.
       Remove any previous pending League-registration line before adding the
       freshly submitted details so repeated tests never create quantity 2. */
    let cart=await fetchLeagueCart(root);
    const existing=(cart.items||[]).filter(item=>Number(item.variant_id)===variantId);
    for(const item of existing){
      const response=await fetch(`${root}cart/change.js`,{
        method:'POST',
        headers:{'Content-Type':'application/json',Accept:'application/json'},
        credentials:'same-origin',
        body:JSON.stringify({id:item.key,quantity:0})
      });
      if(!response.ok)throw new Error('We could not refresh your pending League registration. Please try again.');
    }

    const payload={
      items:[{
        id:variantId,
        quantity:1,
        properties:{
          'Name':marker.name||'',
          'WhatsApp':marker.phone||'',
          'Email':marker.email||'',
          'Level':marker.preference||'',
          'WPPR ranking':marker.wppr||'',
          'Registration':marker.method||'',
          '_MOTM source':'League registration',
          '_MOTM status':'Pending payment'
        }
      }]
    };

    const add=await fetch(`${root}cart/add.js`,{
      method:'POST',
      headers:{'Content-Type':'application/json',Accept:'application/json'},
      credentials:'same-origin',
      body:JSON.stringify(payload)
    });
    if(!add.ok){
      let message='We could not prepare your League registration. Please try again.';
      try{const data=await add.json();message=data?.description||data?.message||message;}catch(_){}
      throw new Error(message);
    }

    cart=await fetchLeagueCart(root);
    notifyLeagueCartUpdate(cart);
    return cart;
  }

  function initLeagueSubmissionTracking(root=document){
    const forms=[...root.querySelectorAll?.('form.motm-league__registration-form')||[]];
    forms.forEach(form=>{
      if(form.dataset.motmLeagueTracking==='1')return;
      form.dataset.motmLeagueTracking='1';
      form.addEventListener('submit',async(event)=>{
        const section=form.closest('#league-registration');
        const marker=leagueMarkerFromForm(form);

        /* Paid Standard Registration never leaves this page. Shopify's Ajax
           Cart API stores the registration details in the cart immediately,
           so there is no native contact-form redirect and no /challenge page. */
        if(section&&leagueMethodRequiresPayment(section,marker.method)){
          event.preventDefault();
          if(form.dataset.motmAjaxBusy==='1')return;
          form.dataset.motmAjaxBusy='1';

          const submit=form.querySelector('[data-motm-league-submit]');
          const errorBox=form.querySelector('[data-motm-league-ajax-error]');
          const originalHtml=submit?.innerHTML||'';
          if(errorBox){errorBox.hidden=true;errorBox.textContent='';}
          if(submit){submit.disabled=true;submit.textContent='PREPARING REGISTRATION…';}

          try{
            clearLeagueSubmissionMarker();
            await prepareLeaguePaidRegistration(section,marker);
            setLeaguePaymentPending(marker);

            const fallback=section.querySelector('[data-motm-league-client-success]');
            form.hidden=true;
            if(fallback)fallback.hidden=false;
            setLeaguePaymentPanel(section,marker);

            /* Deliberately no scrollIntoView, hash navigation or reload here.
               The buyer stays at the exact viewport where they submitted. */
          }catch(error){
            if(errorBox){
              errorBox.textContent=error?.message||'We could not prepare your registration. Please try again.';
              errorBox.hidden=false;
            }
            if(submit){submit.disabled=false;submit.innerHTML=originalHtml;}
            form.dataset.motmAjaxBusy='0';
          }
          return;
        }

        /* Member registrations use a separate hidden Shopify contact-form
           bridge. Keeping the visible form plain prevents Shopify CAPTCHA from
           ever attaching to the paid Standard Registration interaction. */
        event.preventDefault();
        const bridge=section?.querySelector('form.motm-league__member-contact-bridge');
        if(!bridge)return;
        const fields={
          name:marker.name,
          phone:marker.phone,
          email:marker.email,
          preference:marker.preference,
          wppr:marker.wppr,
          method:marker.method
        };
        Object.entries(fields).forEach(([key,value])=>{
          const target=bridge.querySelector(`[data-motm-member-field="${key}"]`);
          if(target)target.value=value||'';
        });
        try{
          history.scrollRestoration='manual';
          sessionStorage.setItem('motmLeagueSubmissionPending',JSON.stringify(strippedMarker(marker)));
        }catch(_){}
        const memberSubmit=bridge.querySelector('[data-motm-member-submit]');
        if(memberSubmit)memberSubmit.click();
        else bridge.requestSubmit?.();
      });
    });
  }

  function initLeaguePaymentAcknowledgement(root=document){
    const section=root.querySelector?.('#league-registration')||document.querySelector('#league-registration');
    if(!section)return;
    const form=section.querySelector('form.motm-league__registration-form');
    const wrap=form?.querySelector('[data-motm-payment-ack]');
    const checkbox=wrap?.querySelector('[data-motm-payment-ack-checkbox]');
    const method=form?.querySelector('[name="contact[registration_method]"]');
    if(!form||!wrap||!checkbox||!method||wrap.dataset.motmPaymentAckReady==='1')return;
    wrap.dataset.motmPaymentAckReady='1';
    const standard=(wrap.dataset.standardMethod||'').trim().toLowerCase();
    const sync=()=>{
      const required=Boolean(standard&&method.value.trim().toLowerCase()===standard);
      wrap.hidden=!required;
      checkbox.disabled=!required;
      checkbox.required=required;
      if(!required)checkbox.checked=false;
    };
    method.addEventListener('change',sync);
    method.addEventListener('input',sync);
    sync();
  }

  function initLeaguePaymentSync(root=document){
    const section=root.querySelector?.('#league-registration')||document.querySelector('#league-registration');
    if(!section)return;
    const registrationForm=section.querySelector('form.motm-league__registration-form');
    const paymentForm=section.querySelector('form.motm-league__payment-form');
    if(!registrationForm||!paymentForm||paymentForm.dataset.motmLeaguePaymentSync==='1')return;
    paymentForm.dataset.motmLeaguePaymentSync='1';

    const map={
      name:'contact[name]',
      phone:'contact[phone]',
      email:'contact[email]',
      preference:'contact[league_preference]',
      wppr:'contact[verified_wppr_ranking]',
      method:'contact[registration_method]'
    };

    const sync=()=>{
      const saved=leagueCartDetails||{};
      Object.entries(map).forEach(([key,sourceName])=>{
        const source=registrationForm.querySelector(`[name="${sourceName}"]`);
        const target=paymentForm.querySelector(`[data-motm-payment-property="${key}"]`);
        if(!target)return;
        const current=source?.value||'';
        target.value=current||saved[key]||'';
      });
    };

    registrationForm.addEventListener('input',sync);
    registrationForm.addEventListener('change',sync);
    paymentForm.addEventListener('submit',sync);
    sync();
  }

  async function initLeaguePaymentFlow(root=document){
    const section=root.querySelector?.('#league-registration')||document.querySelector('#league-registration');
    if(!section)return;
    const panel=section.querySelector('[data-motm-league-payment-panel]');
    if(!panel)return;

    /* A paid registration prepared through Ajax remains visible after a manual
       refresh, but never causes an automatic scroll or navigation. */
    const pending=getLeaguePaymentPending();
    const show=Boolean(pending&&leagueMethodRequiresPayment(section,pending.method));
    if(!show){
      setLeaguePaymentPanel(section,null);
      return;
    }
    const details=await leagueDetailsFromCart(section);
    if(!details){
      clearLeaguePaymentPending();
      setLeaguePaymentPanel(section,null);
      return;
    }
    leagueCartDetails=details;
    setLeaguePaymentPanel(section,{...pending,...details});
    const form=section.querySelector('form.motm-league__registration-form');
    const fallback=section.querySelector('[data-motm-league-client-success]');
    if(form)form.hidden=true;
    if(fallback)fallback.hidden=false;
  }

  function initLeagueReturnRouter(){
    const marker=getLeagueSubmissionMarker();
    if(!marker)return false;

    const url=new URL(location.href);
    const onLeague=Boolean(document.querySelector('#league-registration'));
    if(onLeague)return false;

    /* League currently uses the Events Shopify Page as the underlying
       resource for its alternate template. If hCaptcha drops the view query
       and returns to /pages/events, immediately recover the League view. */
    if(url.pathname.replace(/\/+$/,'')==='/pages/events'){
      const target='/pages/events?view=league&motm_registration=sent';
      location.replace(target);
      return true;
    }
    return false;
  }

  function initLeagueReturnSuccess(root=document){
    const section=root.querySelector?.('#league-registration')||document.querySelector('#league-registration');
    if(!section)return;

    const marker=getLeagueSubmissionMarker();
    const form=section.querySelector('form.motm-league__registration-form');
    const memberSuccess=section.querySelector('[data-motm-league-member-success]');
    const posted=section.querySelector('[data-motm-member-posted-success]')?.value==='true';
    const hasErrors=section.querySelector('[data-motm-member-has-errors]')?.value==='true';

    if(posted&&form&&memberSuccess){
      form.hidden=true;
      memberSuccess.hidden=false;
      clearLeaguePaymentPending();
      setLeaguePaymentPanel(section,null);
      clearLeagueSubmissionMarker();
      const url=new URL(location.href);
      if(url.searchParams.has('motm_registration')){
        url.searchParams.delete('motm_registration');
        history.replaceState({},'',`${url.pathname}${url.search}${url.hash}`);
      }
      if(marker)restoreLeagueViewport(marker);
      return;
    }

    if(hasErrors&&form){
      const errorBox=form.querySelector('[data-motm-league-ajax-error]');
      if(errorBox){
        errorBox.textContent='We could not send the member registration. Please review the details and try again.';
        errorBox.hidden=false;
      }
      clearLeagueSubmissionMarker();
    }
  }

  function initReveal(){if(document.body?.classList.contains('motm-motion-studio'))return;if(matchMedia('(prefers-reduced-motion:reduce)').matches)return;const els=[...document.querySelectorAll('.motm-section,.motm-editorial,.motm-feature-grid')];if(!('IntersectionObserver'in window)){els.forEach(e=>e.classList.add('is-in'));return;}const io=new IntersectionObserver(es=>es.forEach(e=>{if(e.isIntersecting){e.target.classList.add('is-in');io.unobserve(e.target)}}),{threshold:.05,rootMargin:'0px 0px -4%'});els.forEach(el=>{el.classList.add('motm-reveal');if(el.getBoundingClientRect().top<innerHeight*.92)el.classList.add('is-in');else io.observe(el)});}
  function initStudioMotion(root=document){
    if(!document.body?.classList.contains('motm-motion-studio'))return;
    if(matchMedia('(prefers-reduced-motion:reduce)').matches)return;
    const sections=[...root.querySelectorAll('.motm-section,.motm-editorial,.motm-feature-grid')];
    sections.forEach(el=>{if(el.dataset.motmStudioSection)return;el.dataset.motmStudioSection='1';el.classList.add('motm-studio-section');});
    const cards=[...root.querySelectorAll('.motm-feature-card,.motm-plan,.motm-event-card,.motm-partner-premium-card,.motm-product-card,.motm-principle,.motm-rate-card,.motm-competition-preview__format')];
    cards.forEach(el=>{if(el.dataset.motmStudioCard)return;el.dataset.motmStudioCard='1';el.classList.add('motm-studio-card');});
    const targets=[...root.querySelectorAll('.motm-studio-section,.motm-studio-card')].filter(el=>!el.dataset.motmStudioObserved);
    if(!('IntersectionObserver' in window)){targets.forEach(el=>el.classList.add('motm-studio-in'));return;}
    const io=new IntersectionObserver(entries=>entries.forEach(entry=>{if(!entry.isIntersecting)return;entry.target.classList.add('motm-studio-in');io.unobserve(entry.target)}),{threshold:.08,rootMargin:'0px 0px -6% 0px'});
    targets.forEach(el=>{el.dataset.motmStudioObserved='1';if(el.getBoundingClientRect().top<innerHeight*.92)el.classList.add('motm-studio-in');else io.observe(el)});
  }
  ready(()=>{initCursor();initMarquees();initAdaptiveHeader();initFormSuccess();initLeagueSubmissionTracking();initLeaguePaymentAcknowledgement();initLeaguePaymentSync();initLeaguePaymentFlow();if(!initLeagueReturnRouter())initLeagueReturnSuccess();initReveal();initStudioMotion();document.addEventListener('shopify:section:load',e=>{initMarquees();initFormSuccess(e.target||document);initLeagueSubmissionTracking(e.target||document);initLeaguePaymentAcknowledgement(e.target||document);initLeaguePaymentSync(e.target||document);initLeaguePaymentFlow(e.target||document);initLeagueReturnSuccess(e.target||document);initReveal();initStudioMotion(e.target||document);});});
})();


// v7.5: lightweight product variant display refresh.
document.addEventListener('change', (event) => {
  const select = event.target.closest('[data-motm-variant-select]');
  if (!select) return;
  const section = select.closest('.motm-purchase');
  const priceNode = section?.querySelector('[data-motm-product-price]');
  if (!priceNode) return;
  const option = select.selectedOptions?.[0];
  if (!option) return;

  const currency = document.documentElement.lang || 'en';
  const price = Number(option.dataset.price || 0) / 100;
  const compare = Number(option.dataset.comparePrice || 0) / 100;
  const money = new Intl.NumberFormat(currency, {
    style: 'currency',
    currency: (window.Shopify?.currency?.active || 'AED'),
    maximumFractionDigits: 2
  });

  const main = priceNode.querySelector('span');
  if (main) main.textContent = money.format(price);
  let crossed = priceNode.querySelector('s');
  if (compare > price) {
    if (!crossed) {
      crossed = document.createElement('s');
      priceNode.appendChild(crossed);
    }
    crossed.textContent = money.format(compare);
  } else if (crossed) {
    crossed.remove();
  }
});

/* MOTM v8.8.5: navigation unchanged; League submission gets explicit Shopify return recovery and required Handbook acknowledgement. */
